const AUTH_TOKEN_KEY = "auth_token";
const AUTH_TOKEN_TYPE_KEY = "auth_token_type";
const AUTH_USER_KEY = "auth_user";

type StorageKind = "local" | "session";

export type SetAuthTokenOptions = {
  tokenType?: string;
  username?: string;
  rememberMe?: boolean;
};

function getStorage(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readFromAnyStorage(key: string): string | null {
  const local = getStorage("local");
  const session = getStorage("session");

  const localValue = local?.getItem(key);
  if (localValue) return localValue;

  const sessionValue = session?.getItem(key);
  if (sessionValue) return sessionValue;

  return null;
}

function removeFromAllStorages(key: string): void {
  const local = getStorage("local");
  const session = getStorage("session");
  local?.removeItem(key);
  session?.removeItem(key);
}

function decodeBase64Url(value: string): string | null {
  if (typeof window === "undefined" || typeof window.atob !== "function") {
    return null;
  }

  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return window.atob(padded);
  } catch {
    return null;
  }
}

function isJwtLike(token: string): boolean {
  return token.split(".").length === 3;
}

function isTokenExpired(token: string): boolean {
  if (!isJwtLike(token)) return true;

  const parts = token.split(".");
  const payloadBase64 = parts[1];
  const payloadJson = decodeBase64Url(payloadBase64);
  if (!payloadJson) return true;

  try {
    const payload = JSON.parse(payloadJson) as { exp?: unknown };
    if (typeof payload.exp !== "number") return true;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowSeconds;
  } catch {
    return true;
  }
}

export function clearAuthSession(): void {
  removeFromAllStorages(AUTH_TOKEN_KEY);
  removeFromAllStorages(AUTH_TOKEN_TYPE_KEY);
  removeFromAllStorages(AUTH_USER_KEY);
}

export function setAuthToken(
  token: string,
  options?: SetAuthTokenOptions,
): void {
  const trimmedToken = token.trim();
  if (!trimmedToken) {
    clearAuthSession();
    return;
  }

  const rememberMe = Boolean(options?.rememberMe);
  const targetStorage = getStorage(rememberMe ? "local" : "session");
  if (!targetStorage) {
    return;
  }

  // Keep a single active session source (local or session).
  clearAuthSession();

  targetStorage.setItem(AUTH_TOKEN_KEY, trimmedToken);
  targetStorage.setItem(AUTH_TOKEN_TYPE_KEY, options?.tokenType ?? "bearer");
  if (options?.username) {
    targetStorage.setItem(AUTH_USER_KEY, options.username);
  }
}

export function getAuthToken(): string | null {
  const token = readFromAnyStorage(AUTH_TOKEN_KEY);
  if (!token) return null;

  // Defensive cleanup for malformed/expired tokens.
  if (!isJwtLike(token) || isTokenExpired(token)) {
    clearAuthSession();
    return null;
  }

  return token;
}

export function isAuthenticated(): boolean {
  return Boolean(getAuthToken());
}

